import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { StoreLanding } from './StoreLanding';
import { AccountLanding } from './AccountLanding';
import { StorefrontInfo } from './StorefrontInfo';
import { Billing } from './Billing';
import { Team } from './Team';
import { Notifications } from './Notifications';
import { Security } from './Security';
import { SettingsTabs } from './components/SettingsTabs';

/**
 * Settings is shop-scoped: in a chain it renders under `/:shopId/settings/*`,
 * in a single-shop org under the flat `/settings/*`. The two tabs split the
 * page by scope:
 *
 *   - **Store** (`/settings/store/*`) is per-shop — Storefront info,
 *     Billing & plan, and signposts to the per-shop Inventory feed and
 *     Channels pages. Switching shops via the store switcher swaps the
 *     `:shopId` prefix and keeps you on the same sub-page.
 *   - **Account** (`/settings/account/*`) applies to the whole user/org —
 *     team membership, email preferences, password and 2FA. The URL keeps
 *     the `:shopId` prefix for nav consistency, but the content doesn't
 *     vary by shop.
 *
 * The landing pages (Store / Account) only render under their own tab —
 * the page header sits above the tab bar, so it's only present on those
 * landings. Sub-pages render their own headers via `SettingsHeader`.
 */
export function Settings() {
	const { pathname } = useLocation();
	const onLanding = /\/settings\/(store|account)\/?$/.test(pathname);

	return (
		<div className="space-y-8">
			{onLanding ? <SettingsLandingHeader /> : null}
			{onLanding ? <SettingsTabs /> : null}
			<Routes>
				<Route index element={<Navigate to="store" replace />} />
				<Route path="store" element={<StoreLanding />} />
				<Route path="store/storefront" element={<StorefrontInfo />} />
				<Route path="store/billing" element={<Billing />} />
				<Route path="account" element={<AccountLanding />} />
				<Route path="account/team" element={<Team />} />
				<Route path="account/notifications" element={<Notifications />} />
				<Route path="account/security/*" element={<Security />} />
				<Route path="*" element={<Navigate to="store" replace />} />
			</Routes>
		</div>
	);
}

function SettingsLandingHeader() {
	return (
		<header>
			<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Settings</h1>
			<p className="mt-2 text-muted-foreground">Configuration and advanced tools — tucked away so they don&apos;t get in your way.</p>
		</header>
	);
}
